import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import DOMPurify from 'dompurify';

import type { RecordData, Service, Slack } from 'webapp-types';
import { GoalStatus, ModelName } from 'webapp/enums/enums';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';

import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type Goal from 'webapp/models-custom/goal';
import type User from 'webapp/models-custom/user';
import type GoalLogRecord from 'webapp/models/goal-log';

export default class GoalLog extends BufferedModel<GoalLogRecord, RecordData.GoalLog.Serialized> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	get id() {
		return this.model.id;
	}

	get goalId(): number {
		return this.model.goalId;
	}

	get value(): number {
		return this.getBuffered('value');
	}

	set value(value: number) {
		this.setBuffered('value', value);
	}

	get valueStr(): string {
		return this.getBuffered('valueStr');
	}

	get message(): string | null {
		return this.getBuffered('message');
	}

	set message(value: string | null) {
		this.setBuffered('message', value);
	}

	get status(): GoalStatus {
		return this.getBuffered('status');
	}

	set status(value: GoalStatus) {
		this.setBuffered('status', value);
	}

	get delivery(): number[] {
		return this.getBuffered('delivery');
	}

	set delivery(value: number[]) {
		this.setBuffered('delivery', value);
	}

	get recipients(): string[] {
		return this.getBuffered('recipients');
	}

	set recipients(value: string[]) {
		this.setBuffered('recipients', value);
	}

	get slackChannels(): Slack.Channel[] {
		return this.getBuffered('slackChannels');
	}

	set slackChannels(value: Slack.Channel[]) {
		this.setBuffered('slackChannels', value);
	}

	get created(): number {
		return this.getBuffered('created');
	}

	set created(value: number) {
		this.setBuffered('created', value);
	}

	get modified(): number {
		return this.model.modified;
	}

	get modifiedBy(): number {
		return this.model.modifiedBy;
	}

	get changeStr(): string {
		return this.model.changeStr;
	}

	get change(): number {
		return this.model.change;
	}

	get createdBy(): number {
		return this.model.createdBy;
	}

	get isPredefinedMessage(): boolean {
		return this.model.isPredefinedMessage;
	}

	get isDeliveryOn(): boolean {
		return this.getBuffered('isDeliveryOn');
	}

	set isDeliveryOn(value: boolean) {
		this.setBuffered('isDeliveryOn', value);
	}

	@cached
	get goal(): Goal {
		return this.mmGoalsService.findModel(String(this.goalId))!;
	}

	override get validationRules() {
		const rules: Record<string, ValidationRule[]> = {
			status: ['required'],
		};

		if (this.goal.isManualGoal) {
			rules.value = ['required'];
		}

		return rules;
	}

	@cached
	get createdByUser(): User | null {
		return this.mmUsersService.findModel(String(this.createdBy));
	}

	@cached
	get modifiedByUser(): User | null {
		return this.mmUsersService.findModel(String(this.modifiedBy));
	}

	get isCreatedByMe() {
		return this.createdBy === this.currentUserService.numericId;
	}

	@cached
	get createdText() {
		return getFormattedTime(this.created, this.mmSourcesService.defaultSource?.timezone);
	}

	@cached
	get createdTextRelative() {
		return getRelativeTime(this.created);
	}

	@cached
	get modifiedText() {
		return getFormattedTime(this.modified, this.mmSourcesService.defaultSource?.timezone);
	}

	@cached
	get modifiedTextRelative() {
		return getRelativeTime(this.modified);
	}

	get havePermissionToEdit() {
		return this.isCreatedByMe || this.goal?.isOwnedByMe;
	}

	get sanitizedMessage() {
		const message = this.message;

		if (!message) {
			return '';
		}

		// sanitizing only if it's a predefined message from backend
		if (this.isPredefinedMessage) {
			return DOMPurify.sanitize(message, {
				ALLOWED_TAGS: ['b'],
			});
		} else {
			return message;
		}
	}

	get changeIndicatorClass() {
		switch (this.status) {
			case GoalStatus.ON_TRACK:
			case GoalStatus.ACHIEVED:
				return 'done';
			case GoalStatus.AT_RISK:
			case GoalStatus.PARTIAL:
				return 'medium';
			case GoalStatus.OFF_TRACK:
			case GoalStatus.MISSED:
				return 'low';
			default:
				return 'none';
		}
	}

	static override modelName = ModelName.GOAL_LOG;
}
